import {StringArray} from '@src/@interfaces/types';
import {ILocalisation} from '@src/@interfaces/services/Localisation/ILocalisation';
import {ILocalisationComponentToMessagesMap, IMessagesBag} from '@src/@interfaces/services/Localisation/IMessagesBag';
import app from '@src/helpers/app';
import {ILocalisationMessage} from '@src/@interfaces/services/Database';
import {fetchLocalisations, setLocale, setLocalisations} from '@src/_redux/locale/actions';
import {EventPayload} from '@src/@types/services/Events/EventPayload';
import AppSetting from '../Database/Models/AppSetting';
import LocalisationMessage from '../Database/Models/LocalisationMessage';
import {LocalisationEventPayload, LocalisationEvents} from '@src/@types/services/Localisation/Localisation';
import {IListener} from '@src/@interfaces/services/Events';
import MessageSelector from './MessageSelector';
import {IMessageSelector} from '@src/@interfaces/services/Localisation/IMessageSelector';

const changeHtmlLangAttribute = (locale: string) => {
    if (document && document?.documentElement?.tagName === 'HTML') {
        document.documentElement.setAttribute('lang', locale);
    }
};

async function boot(this: Localisation) {
    const db = app('db');
    const dispatch = app().dispatchAction.bind(app());

    const resolveMessages = async () => {
        if (!app('config').get('client.store-data-in-db') as boolean) {
            return;
        }

        const table = db.localisations;
        if (!await table.count()) {
            return;
        }

        const messages = await table.toArray();
        const bag: IMessagesBag = messages.reduce((res: IMessagesBag, item: ILocalisationMessage): IMessagesBag => {
            res[item.locale] ??= {};
            res[item.locale][item.component] ??= {};
            res[item.locale][item.component][item.key] = item.message;

            return res;
        }, {});

        dispatch(setLocalisations(bag));
    };

    const resolveLocales = async () => {
        const setting = await db.settings
            .where({
                'service': 'localisation',
                'name': 'locale',
            })
            .first();

        const currentLocale = String(setting?.value ?? this.fallbackLocale);

        changeHtmlLangAttribute(currentLocale);

        dispatch(setLocale(currentLocale));
    };

    const registerListeners = () => {
        this.listen(LocalisationEvents.SET_MESSAGES, (event, payload) => {
            const messages = payload.messages as IMessagesBag;
            const records = Object.keys(messages).reduce((res: any, locale: string) => {
                return Object.keys(messages[locale]).reduce((res, component: string) => {
                    return Object.keys(messages[locale][component]).reduce((res, key: string) => {
                        const message: string = messages[locale][component][key];

                        res.push([{locale, component, key}, {message}]);

                        return res;
                    }, res);
                }, res);
            }, []);

            LocalisationMessage.updateOrCreateMany(records);
        });

        this.listen(LocalisationEvents.SET_LOCALE, (event, payload) => {
            const locale = payload.newLocale as string;

            AppSetting.updateOrCreate<AppSetting>({
                service: 'localisation',
                name: 'locale',
            }, {
                value: locale
            });

            changeHtmlLangAttribute(locale);
        });

        this.listen(LocalisationEvents.SET_FALLBACK_LOCALE, (event, payload) => {
            const locale = payload.newLocale as string;

            AppSetting.updateOrCreate<AppSetting>({
                service: 'localisation',
                name: 'fallback-locale',
            }, {
                value: locale
            });
        });
    };

    await Promise.all([
        resolveLocales(),
        resolveMessages(),
    ]);

    registerListeners();
}

export default class Localisation implements ILocalisation {
    protected locale = 'en';
    protected fallbackLocale = 'en';
    protected messages: IMessagesBag = {};

    protected selectorInstance?: IMessageSelector;

    get(key: string, replace?: StringArray<string | number>, defaultValue?: string | null, locale?: string): string | null {
        for (const lang of this.getLocalesArray(locale)) {
            const result = this.getMessage(key, replace ?? {}, lang);
            if (result !== null) {
                return this.removeRedundantQuotes(result);
            }
        }

        return defaultValue ?? null;
    }

    choice(
        key: string,
        number: number | any[] | Record<any, any>,
        replace?: StringArray<string | number>,
        defaultValue?: string | null,
        locale?: string
    ): string | null {
        for (const lang of this.getLocalesArray(locale)) {
            const result = this.choiceMessage(key, number, replace, null, lang);
            if (result !== null) {
                return this.removeRedundantQuotes(result);
            }
        }

        return defaultValue ?? null;
    }

    getMessages(locale?: string): ILocalisationComponentToMessagesMap {
        return this.messages[locale ?? this.getLocale()] ?? {};
    }

    setMessages(messages: IMessagesBag): void {
        this.fireEvent(LocalisationEvents.SET_MESSAGES, {service: this, messages: messages});

        this.messages = messages;
    }

    getLocale() {
        return this.locale;
    }

    setLocale(locale: string): void {
        this.fireEvent(LocalisationEvents.SET_LOCALE, {
            service: this,
            oldLocale: this.getLocale(),
            newLocale: locale
        });

        this.locale = locale;
    }

    getFallbackLocale() {
        return this.fallbackLocale;
    }

    setFallbackLocale(locale: string): void {
        this.fireEvent(LocalisationEvents.SET_FALLBACK_LOCALE, {
            service: this,
            oldLocale: this.getFallbackLocale(),
            newLocale: locale
        });

        this.fallbackLocale = locale;
    }

    listen(eventName: LocalisationEvents, listener: IListener<LocalisationEventPayload>): void {
        app('events').listen(eventName, listener);
    }

    accessor() {
        return app('dot-accessor');
    }

    protected getMessage(key: string, replace: StringArray<string | number>, locale: string, defaultValue?: string | null): string | null {
        const messages = this.getMessages(locale);
        const message = this.accessor().get(messages, key);
        if (typeof message === 'string') {
            return this.applyReplacers(message, replace || {});
        }

        return defaultValue ?? null;
    }

    protected choiceMessage(
        key: string,
        number: number | any[] | Record<any, any>,
        replace?: StringArray<string | number>,
        defaultValue?: string | null,
        locale?: string
    ): string | null {
        const message = this.getMessage(key, replace ?? {}, locale ??= this.getLocale(), defaultValue);
        if (typeof message !== 'string') {
            return message;
        }

        return this.selectMessage(message, number, replace, locale);
    }

    protected removeRedundantQuotes(message: string): string {
        return message.replace(/\\\[/g, '[').replace(/\\]/g, ']');
    }

    protected applyReplacers(message: string, replace: StringArray<string | number> = {}): string {
        return Object.keys(replace)
            .sort((a, b) => b.length - a.length)
            .reduce((res, key) => {
                return res.replace(new RegExp(`:${key}`, 'gim'), String(replace[key]));
            }, message);
    }

    protected fireEvent(eventName: LocalisationEvents, payload?: EventPayload): any[] {
        return app('events').dispatch(eventName, payload);
    }

    protected getLocalesArray(locale?: string): string[] {
        const currentLocale = this.getLocale();
        const fallbackLocale = this.getFallbackLocale();

        const map: Record<string, number> = {};
        const locales = [locale, currentLocale, fallbackLocale];
        const result = [];
        for (const lang of locales) {
            if (lang && !(lang in map)) {
                map[lang] = result.push(lang);
            }
        }

        return result;
    }

    protected selectMessage(
        message: string,
        number: number | any[] | Record<any, any>,
        replace?: StringArray<string | number>,
        locale?: string
    ): string {
        if (Array.isArray(number)) {
            number = number.length;
        } else if (['number', 'string'].includes(typeof number)) {
            number = +number;
        } else {
            number = Object.keys(number).length;
        }

        replace ??= {};
        replace.count = number;

        return this.applyReplacers(
            this.getSelector().choose(message, number, locale ?? this.getLocale()),
            replace
        );
    }

    protected getSelector(): IMessageSelector {
        return this.selectorInstance ??= new MessageSelector();
    }

    async boot() {
        await boot.call(this);
    }

    async loadMessages() {
        if (!Object.keys(this.getMessages()).length) {
            app().dispatchAction(fetchLocalisations());
        }
    }
}
